import { useFormik } from "formik";
import * as Yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { OTPInput } from "../../components/OTPInput";
import { useEffect, useState } from "react";
import axiosToken from "../../api/axiosClient";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { MdVerified } from "react-icons/md";
import logo200 from "../../assets/logo/logo-200.png";

const RegisterVerificationPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [focusOptInput, setFocusOptInput] = useState(true);

  const codeLength = 8;
  const { callback } = useParams(); // récupère callback dynamique depuis l'URL

  const [emailVerified, setEmailVerified] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setEmailVerified(false);

    const email = localStorage.getItem("email");
    const authToken = localStorage.getItem("authToken");

    setEmail(email || "");
    setAuthToken(authToken || "");

    if (!email || !authToken) {
      console.error("No email in session, redirecting to register");
      toast.error("Aucun email trouvé, veuillez vous inscrire.", {
        position: "bottom-left",
      });

      setTimeout(() => {
        navigate("/register"); // redirige vers l'inscription si pas d'email en session
      }, 1000);
    }
  }, [navigate]);

  const formik = useFormik({
    initialValues: {
      otp: "",
    },
    validationSchema: Yup.object({
      otp: Yup.string()
        .length(codeLength, `Le code doit contenir ${codeLength || 6} chiffres`)
        .matches(new RegExp(`^\\d{${codeLength || 6}}$`), "Code invalide")
        .required("Code requis"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const data = {
          code: values.otp,
          token: authToken,
        };
        // console.log("Connexion :", data);

        const response = await axiosToken.post("/user/verify", data);

        if (response.status === 200) {
          toast.success("Email vérifié avec succès");
          localStorage.removeItem("authToken");
          localStorage.removeItem("email");

          setEmailVerified(true);
          return;
        } else {
          setFocusOptInput(true);
          toast.error("Erreur lors de la vérification de l'email", {
            position: "bottom-left",
          });

          console.error("Error verifying email");
        }

        setLoading(false);
      } catch (error) {
        console.error(
          "Erreur lors de la vérification de l'email. Erreur: ",
          error
        );
        toast.error(
          error?.response?.data?.error ||
            "Erreur lors de la vérification de l'email"
        );
        setLoading(false);
      }
    },
  });

  const resendRegisterVerification = async () => {
    setLoading(true);

    setEmailVerified(false);
    if (!email) {
      toast.error("Aucun email trouvé pour renvoyer le code");
      console.error("No email found to resend code");
      return;
    }

    console.log("Resend code to email");

    const response = await axiosToken.post(
      "/user/resendEmailVerificationCode",
      { email }
    );

    if (response.status === 200) {
      // Gérer la réponse, par exemple stocker le token
      localStorage.setItem("authToken", response.data.token);
      setAuthToken(response.data.token || "");

      formik.setFieldValue("otp", "");
      formik.setFieldTouched("otp", false);
      setFocusOptInput(true);

      toast.success("Code renvoyé avec succès");
      console.log("Code resent successfully");
    } else {
      toast.error("Erreur lors du renvoi du code");
      console.error("Error resending code");
    }
    setLoading(false);
  };

  document.title = "Validation du mail || Barberlys";

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-xl bg-white flex justify-center shadow-xl p-8">
        <div className="max-w-md">
          {/* Header */}
          <div className="mb-6 text-center">
            {/* <span className="text-sm bg-secondary-50 text-secondary-700 px-3 py-1 font-medium">
              Validation
            </span> */}
            <Link to={`/index`} className="cursor-pointer">
              <img src={logo200} className="m-auto" />
            </Link>
            <h2 className="text-2xl font-semibold mt-3">
              Valider l'adresse e-mail
            </h2>
          </div>

          <div className="mb-4">
            <p className="text-center text-gray-300">
              Un e-mail de validation a été envoyé à votre adresse :
              <span className="font-bold">
                {email ? ` ${email}` : " (aucun email trouvé)"}
              </span>
            </p>
          </div>

          {!emailVerified ? (
            <motion.div
              key="enterCode"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.4 }}
            >
              {/* Form */}
              <form
                className="space-y-5  justify-center"
                onSubmit={formik.handleSubmit}
              >
                <div className="space-y-1">
                  <OTPInput
                    codeLength={codeLength}
                    focus={focusOptInput}
                    setFocus={setFocusOptInput}
                    value={formik.values.otp}
                    onChange={formik.setFieldValue}
                  />
                  {formik.touched.otp && formik.errors.otp && (
                    <p className="text-red-400 text-sm text-center">
                      {formik.errors.otp}
                    </p>
                  )}
                </div>

                <div className="text-center text-sm text-gray-400">
                  Vous n'avez pas reçu le code ?{" "}
                  <button
                    disabled={loading}
                    type="button"
                    className="text-secondary-100 hover:text-secondary-50 font-medium p-1"
                    onClick={() => resendRegisterVerification()}
                  >
                    {loading && (
                      <span className="animate-spin  rounded-full h-4 w-4 border-2 border-secondary-100 border-t-transparent"></span>
                    )}
                    Renvoyer
                  </button>
                </div>

                <div className="w-full flex justify-between items-center mb-6">
                  {/* Button retour  */}
                  <button
                    type="button"
                    onClick={() => {
                      navigate(-1);
                    }}
                    className="bg-white/20 hover:bg-white/20 text-white py-2 px-4 font-semibold transition-all"
                  >
                    Retour
                  </button>

                  <button
                    disabled={
                      formik.values.otp.length !== codeLength || loading
                    }
                    type="submit"
                    className={`${
                      formik.values.otp.length !== codeLength || loading
                        ? "bg-secondary-200"
                        : "bg-secondary-400"
                    }  hover:bg-secondary-500 text-white py-2 px-4 font-semibold transition-all`}
                  >
                    {loading ? (
                      <div className="flex flex-row justify-center items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-t-4 border-b-4 border-white" />
                        <span>Vérification...</span>
                      </div>
                    ) : (
                      "Vérifier"
                    )}
                  </button>
                </div>

                <p className="text-center text-sm text-gray-500 mt-4">
                  Déjà inscrit ?{" "}
                  <Link
                    to="/login"
                    className="text-secondary hover:text-secondary-700"
                  >
                    Se connecter
                  </Link>
                </p>
              </form>
            </motion.div>
          ) : (
            <motion.div
              key="verified"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.4 }}
            >
              <div className="text-center space-y-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1.2 }}
                  transition={{ type: "spring", stiffness: 300, damping: 10 }}
                  className="flex justify-center"
                >
                  <MdVerified
                    size={64}
                    className="text-green-400 drop-shadow-lg"
                  />
                </motion.div>

                <h3 className="text-xl font-semibold">
                  Adresse e-mail vérifiée !
                </h3>
                <p className="text-gray-500">
                  Vous pouvez maintenant vous connecter.
                </p>
                <button
                  onClick={() => navigate(`/${callback || "login"}`)}
                  className="bg-secondary-300 hover:bg-secondary-400 text-white py-2 px-4 font-semibold transition-all"
                >
                  Continuer
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterVerificationPage;
